import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useSittingStore from '../../../stores/sittingStore';
import useVisitStore from '../../../stores/visitStore';
import useSittingActions from '../../../hooks/useSittingActions';
import { useTheme } from '../../../theme/ThemeContext';
import {
  cancelSitting as apiCancelSitting,
  scheduleAppointmentForItem,
} from '../../../services/api/patientPlanApi';
import { listMyActionPending } from '../../../services/api/visitActionPendingApi';
import {
  INTENTS,
  INTENT_CATEGORY_PALETTE,
} from '../../../constants/visitActionPending';
import { ROUTES } from '../../../constants/routes';
import CollectReferralPaymentDialog from '../../visit/CollectReferralPaymentDialog';
import ReferralResolveDialog from '../../visit/ReferralResolveDialog';
import eventBus from '../../../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../../../services/realtime/RealtimeSyncService';
import { visitPaymentDueRupees } from '../../../utils/visitMoney';
import { getTodayIST } from '../../../utils/dateIST';
import RoleTaskBadge from './RoleTaskBadge';
import UndoToast from './UndoToast';
import useDeferredAction from './useDeferredAction';
import SchedulePickerDialog from './SchedulePickerDialog';
import { parseReceptionistQuery } from './parseReceptionistQuery';
import { PAYMENT_MODES } from '../../../constants/visitMetadata';
import { PhoneButton } from '../../common';
import { openReceiptForCollectResult, openReceiptForBatch } from '../../../services/receiptHelper';
import { collectBundledPayment } from '../../../services/api/visitActionPendingApi';

/**
 * ReceptionistTaskWidget — action-pending pill on the right edge of
 * the receptionist's screen.
 *
 * Two attention sources are merged behind one badge:
 *   1. Awaiting-payment sittings (Procedure Queue) → "Payment to collect"
 *   2. Visits parked via the action-pending overlay (₹0 referrals,
 *      collected-but-needs-forward, etc.) → "Forward to doctor"
 *
 * Badge count = distinct visits with sittings + parked visits.
 * Live updates ride on existing WS handlers — sittings via
 * sittingStore.actionPendingSittings; parked visits via
 * VISIT_ACTION_PENDING_* event refetch of listMyActionPending.
 */

function formatRupees(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function visitStatusLabel(status) {
  if (!status) return '—';
  const u = String(status).toUpperCase();
  if (u === 'IN_PROGRESS') return 'In consult';
  if (u === 'QUEUE') return 'Waiting';
  if (u === 'COMPLETED') return 'Checked out';
  if (u === 'CANCELLED') return 'Cancelled';
  return u;
}

function chipCost(sitting) {
  const raw =
    sitting?.final_cost
    ?? sitting?.estimated_cost
    ?? sitting?.effective_cost_snapshot
    ?? 0;
  return Number(raw) || 0;
}

function todayYmd() {
  return getTodayIST();
}
function chipDateYmd(s) {
  const ref = s?.scheduled_for || s?.created_at || null;
  if (!ref) return null;
  return String(ref).slice(0, 10);
}
function isPastChip(s, today = todayYmd()) {
  const d = chipDateYmd(s);
  return d != null && d < today;
}
function isFutureChip(s, today = todayYmd()) {
  const d = chipDateYmd(s);
  return d != null && d > today;
}
// A receptionist group is "past" when every sitting on the visit is
// dated before today (i.e. yesterday's action-pending lingering into
// today). Mixed groups stay in the per-visit Q-X pill set so the
// receptionist can still collect on today's chip without leaving the
// Past pill view.
function isPastGroup(group) {
  const today = todayYmd();
  const sittings = group?.sittings || [];
  if (sittings.length === 0) return false;
  return sittings.every((s) => isPastChip(s, today));
}
function isFutureGroup(group) {
  const today = todayYmd();
  const sittings = group?.sittings || [];
  if (sittings.length === 0) return false;
  return sittings.every((s) => isFutureChip(s, today));
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatShortDate(ymd) {
  if (!ymd) return '';
  const parts = ymd.split('-');
  if (parts.length !== 3) return ymd;
  const month = SHORT_MONTHS[Number(parts[1]) - 1] || '';
  return `${Number(parts[2])} ${month}`.trim();
}

// Pick the temporal label shown next to the Q-N on each group header.
// Today is the recognition anchor; past/future groups carry an explicit
// date so the receptionist sees WHEN, not just THAT it's not today.
function groupTemporalLabel(group) {
  if (!group || !group.sittings || group.sittings.length === 0) return null;
  if (isPastGroup(group)) {
    const dates = group.sittings.map(chipDateYmd).filter(Boolean).sort();
    return { icon: '🗓', text: formatShortDate(dates[0]), tone: 'other' };
  }
  if (isFutureGroup(group)) {
    const dates = group.sittings.map(chipDateYmd).filter(Boolean).sort();
    return { icon: '📅', text: formatShortDate(dates[0]), tone: 'other' };
  }
  // Today is the default — surface the word so the receptionist's eye
  // jumps to it as the actionable bucket without parsing a date.
  return { icon: '🕒', text: 'Today', tone: 'today' };
}

// Per-sitting "Book slot" and "Take + Book" actions are hidden for
// now while the scheduling UX is reworked. Flip back to `true` when
// the new booking flow is ready; the SchedulePickerDialog + handlers
// stay wired up below so nothing else needs to change.
const SHOW_PER_SITTING_BOOKING_ACTIONS = false;

// Temporal pills share the widget's amber accent (Today) or a quiet
// slate (past/future). The previous teal/sky/slate trio drifted from
// the surface's palette and made the card feel stitched together.
const TEMPORAL_TONE = {
  today: { bg: '#fef3c7', fg: '#92400e', darkBg: 'rgba(217,119,6,0.18)', darkFg: '#fcd34d' },
  other: { bg: '#f1f5f9', fg: '#475569', darkBg: 'rgba(148,163,184,0.16)', darkFg: '#cbd5e1' },
};

// Inject the entrance keyframe once per page load. Respects the
// reduced-motion preference: under `(prefers-reduced-motion: reduce)`,
// the animation collapses to an instant state change.
if (typeof document !== 'undefined' && !document.getElementById('rxw-keyframes')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'rxw-keyframes';
  styleEl.textContent = `
    @keyframes rxw-fade-in {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .rxw-fade-in { animation: none !important; }
      .rxw-anim { transition: none !important; }
    }
  `;
  document.head.appendChild(styleEl);
}

export default function ReceptionistTaskWidget({ viewer, verticalAnchorPx }) {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const actionPendingSittings = useSittingStore((s) => s.actionPendingSittings);
  const fetchActionPendingSittings = useSittingStore((s) => s.fetchActionPendingSittings);

  // Doctor roster (for resolving target_doctor_id → name + qualification
  // on referral cards). The store caches the result; we pre-warm if empty.
  const doctors = useVisitStore((s) => s.doctors);
  const fetchDoctors = useVisitStore((s) => s.fetchDoctors);
  useEffect(() => {
    if ((!doctors || doctors.length === 0) && typeof fetchDoctors === 'function') {
      fetchDoctors().catch(() => {});
    }
  }, [doctors, fetchDoctors]);
  const doctorById = useMemo(() => {
    const map = new Map();
    for (const d of doctors || []) {
      const id = d.member_id || d.doctor_id;
      if (id) map.set(id, d);
    }
    return map;
  }, [doctors]);
  const formatDoctorLabel = useCallback((doctorId) => {
    if (!doctorId) return 'next doctor';
    const d = doctorById.get(doctorId);
    if (!d) return `Dr ${doctorId.slice(0, 8)}…`;
    const fullName = (d.name || `${d.first_name || ''} ${d.last_name || ''}`).trim();
    const namePart = fullName || d.display_name || doctorId.slice(0, 8);
    const qualBits = [d.qualification, d.specialization].filter(Boolean).join(', ');
    return qualBits ? `Dr. ${namePart} (${qualBits})` : `Dr. ${namePart}`;
  }, [doctorById]);

  // Forward-action dialog targets — receptionist takes action without
  // leaving the dock.
  const [referralCollect, setReferralCollect] = useState(null);
  const [referralResolve, setReferralResolve] = useState(null);

  // Action-pending visits (parked referrals + bundled-payment intents).
  // Independent feed from the sitting list — keeps the badge count
  // accurate even when the parked visit has no awaiting_payment sitting.
  const [actionVisits, setActionVisits] = useState([]);
  const reloadActionVisits = useCallback(async () => {
    try {
      const res = await listMyActionPending();
      setActionVisits(res?.visits || []);
    } catch {
      // Soft-fail: widget keeps showing sittings even if the overlay
      // endpoint blips.
    }
  }, []);

  // Initial fetch — WS handlers keep it warm afterwards.
  useEffect(() => {
    if (typeof fetchActionPendingSittings === 'function') {
      fetchActionPendingSittings().catch(() => {});
    }
    reloadActionVisits();
  }, [fetchActionPendingSittings, reloadActionVisits]);

  // Live refresh of the action-visits feed on overlay events
  // (mirrors what ActionPendingPage subscribes to).
  useEffect(() => {
    const offSet = eventBus.on(
      REALTIME_EVENTS.VISIT_ACTION_PENDING_SET, reloadActionVisits,
    );
    const offResolved = eventBus.on(
      REALTIME_EVENTS.VISIT_ACTION_PENDING_RESOLVED, reloadActionVisits,
    );
    const offBundled = eventBus.on(
      REALTIME_EVENTS.VISIT_BUNDLED_PAYMENT_COLLECTED, reloadActionVisits,
    );
    return () => { offSet?.(); offResolved?.(); offBundled?.(); };
  }, [reloadActionVisits]);

  const reload = () => {
    if (typeof fetchActionPendingSittings === 'function') {
      fetchActionPendingSittings().catch(() => {});
    }
    reloadActionVisits();
  };

  const { handleAction: rawHandleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
  });

  // Smart-input filter — one text box accepts patient name, mobile
  // fragment, or a Q-token (q1 / Q1 / Q-1 / q 1 / bare 1-3 digit).
  // `todayOnly` is the sticky temporal chip — Today is the everyday
  // lens (focus on actionable work). It defaults ON so the receptionist
  // sees only today's actionable work first; past + future are one tap
  // away via the "🕒 Today" toggle (which renders only when such items
  // exist) and carry their own row labels when revealed.
  const [query, setQuery] = useState('');
  const [todayOnly, setTodayOnly] = useState(true);
  const [inputFocused, setInputFocused] = useState(false);
  const parsedQuery = useMemo(() => parseReceptionistQuery(query), [query]);
  const filterActive = parsedQuery.intent !== 'empty' || todayOnly;
  const handleAction = useCallback(
    (event) => rawHandleAction({ ...event, skipConfirm: filterActive }),
    [rawHandleAction, filterActive],
  );

  // Group sittings by visit_id; sort visits by queue_number where known.
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of actionPendingSittings || []) {
      const key = s.visit_id || `_unbound_${s.sitting_id}`;
      if (!map.has(key)) {
        map.set(key, {
          visit_id: s.visit_id,
          patient_name: s.patient_name || '—',
          patient_id: s.patient_id,
          patient_mobile: s.patient_mobile || '',
          queue_number: s.queue_number ?? null,
          visit_doctor_name: s.visit_doctor_name || s.assigned_to_name || null,
          visit_status: s.visit_status || null,
          sittings: [],
          total: 0,
        });
      }
      const entry = map.get(key);
      entry.sittings.push(s);
      entry.total += chipCost(s);
    }
    // Primary sort: representative date DESC so future-scheduled and
    // today's groups float above past-dated lingering work. Within the
    // same date, fall back to queue number ASC so today's Q-1 sits
    // above today's Q-2. Groups with no date sort with today.
    const groupSortKey = (g) => {
      const dates = (g.sittings || []).map(chipDateYmd).filter(Boolean).sort();
      return dates.length > 0 ? dates[dates.length - 1] : todayYmd();
    };
    return Array.from(map.values()).sort((a, b) => {
      const da = groupSortKey(a);
      const db = groupSortKey(b);
      if (da !== db) return db.localeCompare(da); // newest first
      const qa = a.queue_number ?? 9999;
      const qb = b.queue_number ?? 9999;
      return qa - qb;
    });
  }, [actionPendingSittings]);

  // Chip visibility — render Today/Past chips only when there's at
  // least one group of that temporal bucket. Avoids dead toggles.
  const hasPastPending = useMemo(() => groups.some(isPastGroup), [groups]);
  const hasFuturePending = useMemo(() => groups.some(isFutureGroup), [groups]);

  // Predicate derived once from the parsed query — used for groups,
  // referral visits, and the typeahead alike so the three surfaces
  // stay consistent.
  const matchesParsed = useCallback((entry) => {
    switch (parsedQuery.intent) {
      case 'empty':
        return true;
      case 'queue':
        return entry.queue_number === parsedQuery.value;
      case 'mobile':
        return (entry.patient_mobile || '').includes(parsedQuery.fragment);
      case 'queueOrMobile':
        return entry.queue_number === parsedQuery.value
          || (entry.patient_mobile || '').includes(parsedQuery.fragment);
      case 'name':
        return (entry.patient_name || '').toLowerCase().includes(parsedQuery.text);
      default:
        return true;
    }
  }, [parsedQuery]);

  const visibleGroups = useMemo(() => {
    const base = todayOnly
      ? groups.filter((g) => !isPastGroup(g) && !isFutureGroup(g))
      : groups;
    return base.filter(matchesParsed);
  }, [groups, todayOnly, matchesParsed]);

  // Top-5 typeahead suggestions. Empty when there's no query so the
  // dropdown only appears once the user starts typing.
  const suggestions = useMemo(() => {
    if (parsedQuery.intent === 'empty') return [];
    const ranked = [];
    for (const g of visibleGroups) {
      // Consistent visual layout across every suggestion — only the
      // leading icon differs to telegraph WHY this row matched
      // (queue / mobile / name). The label, subtitle structure, and
      // padding stay identical so the dropdown doesn't reshape itself
      // as the receptionist's query type changes.
      let icon;
      let canonical;
      if (parsedQuery.intent === 'queue' || parsedQuery.intent === 'queueOrMobile') {
        icon = '🎫';
        canonical = g.queue_number != null
          ? `Q-${g.queue_number}`
          : (g.patient_name || '');
      } else if (parsedQuery.intent === 'mobile') {
        icon = '📱';
        // Commit to the full mobile so the filter stays in mobile mode
        // — clicking the suggestion mustn't swap the lens to queue.
        canonical = g.patient_mobile || g.patient_name || '';
      } else {
        icon = '👤';
        // Same logic for name: commit to the patient name, not Q-N,
        // so the input continues to act as a name filter.
        canonical = g.patient_name || '';
      }
      const label = g.queue_number != null
        ? `Q-${g.queue_number} · ${g.patient_name}`
        : (g.patient_name || '—');
      const subParts = [visitStatusLabel(g.visit_status)];
      if (g.visit_doctor_name) subParts.push(`Dr. ${g.visit_doctor_name}`);
      if (parsedQuery.intent === 'mobile' && g.patient_mobile) {
        subParts.push(g.patient_mobile);
      }
      ranked.push({
        key: g.visit_id || g.sittings?.[0]?.sitting_id,
        icon,
        label,
        sub: subParts.filter(Boolean).join(' · '),
        canonical,
      });
      if (ranked.length >= 5) break;
    }
    return ranked;
  }, [parsedQuery, visibleGroups]);

  // Apply the same parsed predicate to referral visits so the typed
  // query narrows both rails of the panel together. Referrals are
  // always today's work (no scheduled_for), so the today-only chip
  // doesn't hide them.
  const visibleActionVisits = useMemo(
    () => (actionVisits || []).filter((v) => matchesParsed({
      patient_name: v.patient_name,
      patient_mobile: v.patient_mobile,
      queue_number: v.queue_number,
    })),
    [actionVisits, matchesParsed],
  );

  // Unfiltered totals still drive the badge count so the dock pill
  // reflects real outstanding work, not the user's local search view.
  const sittingsCount = groups.length;
  const actionVisitsCount = (actionVisits || []).length;
  const totalCount = sittingsCount + actionVisitsCount;
  const visibleSittingsCount = visibleGroups.length;
  const visibleActionVisitsCount = visibleActionVisits.length;

  // Multi-select scoped to a single visit. Receptionists collect
  // payment one patient at a time at the counter — ticking across
  // visits made the aggregate "Take ₹X" button confusing (whose
  // money?). Tracking the visitId alongside the sitting ids forces
  // selection to belong to exactly one patient.
  const [selection, setSelection] = useState({ visitId: null, sittingIds: new Set() });
  // Per-card refs so we can scroll the selected card into view when
  // the inline payment panel opens — otherwise it can appear below
  // the panel fold and feel disconnected.
  const cardRefs = useRef({});
  useEffect(() => {
    if (!selection.visitId) return;
    const node = cardRefs.current[selection.visitId];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selection.visitId]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchError, setBatchError] = useState('');
  // No default — the receptionist must explicitly pick one. Empty
  // string blocks submission until a method is chosen.
  const [paymentMode, setPaymentMode] = useState('');

  // Cancel flow — sitting_id of the row currently showing the
  // "Why?" reason picker; null when no row is in the cancel mode.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelOtherText, setCancelOtherText] = useState('');
  // Inline error banner for failed cancels (non-blocking; auto-clears
  // after 8s). Surface failures so a 403 / network error doesn't leave
  // the row silently in place.
  const [cancelError, setCancelError] = useState(null);
  const { pending: deferredCancels, schedule: scheduleCancel, undo: undoCancel } = useDeferredAction();

  useEffect(() => {
    if (!cancelError) return undefined;
    const t = setTimeout(() => setCancelError(null), 8000);
    return () => clearTimeout(t);
  }, [cancelError]);

  const fireCancel = (sitting, reason) => {
    setCancelTarget(null);
    setCancelOtherText('');
    const procName = sitting?.procedure_name || sitting?.procedure_snapshot?.name || 'procedure';
    scheduleCancel(
      async () => {
        const r = await apiCancelSitting(sitting.plan_id, sitting.sitting_id, { reason });
        if (r?.success) {
          // Telemetry — receptionist actually let the cancel land.
          // eslint-disable-next-line no-console
          console.info('metric=receptionist_cancel_undo_used value=expired');
          reload();
        } else {
          setCancelError({
            sittingId: sitting.sitting_id,
            procName,
            message: r?.error || 'Could not cancel. Please try again or ask an admin.',
          });
        }
      },
      { delayMs: 5000, label: `Cancelling ${procName}`, key: sitting.sitting_id },
    );
  };

  // Scheduling flow — `scheduleTarget = { sitting, variant }`. variant is
  // 'schedule' (slot only) or 'collectSchedule' (slot + payment in one
  // transaction). Both reuse SchedulePickerDialog.
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const collectPaymentAction = useSittingStore((s) => s.collectPayment);

  const closeScheduleDialog = () => {
    if (scheduleBusy) return;
    setScheduleTarget(null);
    setScheduleError('');
  };

  const handleScheduleConfirm = async ({ slot_date, slot_time, payment_mode, amount }) => {
    if (!scheduleTarget) return;
    const { sitting, variant } = scheduleTarget;
    setScheduleBusy(true);
    setScheduleError('');
    try {
      if (variant === 'collectSchedule') {
        const body = {
          amount: String(amount ?? chipCost(sitting)),
          payment_mode,
          appointment_slot_date: slot_date,
          appointment_slot_time: slot_time,
          waiver_amount: '0',
        };
        const r = await collectPaymentAction(sitting.plan_id, sitting.sitting_id, body);
        if (!r?.success) {
          setScheduleError(r?.error || 'Could not collect & schedule.');
          return;
        }
        // eslint-disable-next-line no-console
        console.info('metric=receptionist_collect_and_schedule value=ok');
        await openReceiptForCollectResult(r);
      } else {
        const itemId = sitting.item_id || sitting.plan_item_id;
        if (!itemId) {
          setScheduleError('Missing plan item id on this sitting; refresh and retry.');
          return;
        }
        const r = await scheduleAppointmentForItem(sitting.plan_id, itemId, {
          slot_date, slot_time,
        });
        if (!r?.success) {
          setScheduleError(r?.error || 'Could not schedule appointment.');
          return;
        }
        // eslint-disable-next-line no-console
        console.info('metric=receptionist_schedule_only value=ok');
      }
      setScheduleTarget(null);
      reload();
    } finally {
      setScheduleBusy(false);
    }
  };

  // Toggling a checkbox in a different visit replaces the prior
  // selection rather than augmenting it — payments are per-patient,
  // never cross-visit.
  const toggleSelect = (sitting) => {
    setSelection((prev) => {
      if (prev.visitId !== sitting.visit_id) {
        return { visitId: sitting.visit_id, sittingIds: new Set([sitting.sitting_id]) };
      }
      const next = new Set(prev.sittingIds);
      if (next.has(sitting.sitting_id)) next.delete(sitting.sitting_id);
      else next.add(sitting.sitting_id);
      return next.size === 0
        ? { visitId: null, sittingIds: next }
        : { visitId: prev.visitId, sittingIds: next };
    });
  };

  const selectAllInGroup = (group) => {
    setSelection({
      visitId: group.visit_id,
      sittingIds: new Set(group.sittings.map((s) => s.sitting_id)),
    });
  };
  const clearSelection = () => setSelection({ visitId: null, sittingIds: new Set() });

  const selectedSittings = useMemo(
    () => (actionPendingSittings || []).filter((s) => selection.sittingIds.has(s.sitting_id)),
    [actionPendingSittings, selection],
  );
  const selectedTotal = selectedSittings.reduce((sum, s) => sum + chipCost(s), 0);
  const selectedPatientName = useMemo(() => {
    if (!selection.visitId) return '';
    const grp = groups.find((g) => g.visit_id === selection.visitId);
    return grp?.patient_name || '';
  }, [groups, selection.visitId]);

  const collectSelected = async () => {
    if (selectedSittings.length === 0 || batchBusy) return;
    if (!paymentMode) {
      setBatchError('Please select a payment method before collecting.');
      return;
    }
    setBatchBusy(true);
    setBatchError('');
    // One BUNDLED payment for all selected procedures (they're always on a
    // single visit — see toggleSelect). The backend mints ONE shared
    // receipt_number, so a 2-procedure collect yields ONE combined receipt
    // listing both — not separate receipts. Mirrors BundledPaymentSheet.
    const sittingPayments = selectedSittings.map((s) => ({
      plan_id: s.plan_id,
      sitting_id: s.sitting_id,
      amount: Number(chipCost(s)) || 0,
    }));
    let res;
    try {
      res = await collectBundledPayment(selection.visitId, {
        payment_mode: paymentMode,
        sitting_payments: sittingPayments,
      });
    } catch (err) {
      setBatchBusy(false);
      setBatchError(err?.response?.data?.detail || err.message || 'Could not collect payment.');
      return;
    }
    setBatchBusy(false);
    clearSelection();
    if (res?.errors?.length) {
      setBatchError(
        `${res.errors.length} of ${sittingPayments.length} failed. Refresh and retry the failures.`,
      );
    }
    // ONE combined receipt for 2+ payments; single receipt for 1.
    await openReceiptForBatch(res?.batch_payment_ids);
    reload();
  };

  // Bulk-cancel for a whole visit's sittings. Same undo-toast mechanism
  // as single-sitting cancel, keyed by visit_id so the receptionist
  // gets one consolidated "Cancelling N procedures for {patient}" toast.
  const [cancelGroupTarget, setCancelGroupTarget] = useState(null);
  const [cancelGroupOtherText, setCancelGroupOtherText] = useState('');
  const fireCancelGroup = (group, reason) => {
    setCancelGroupTarget(null);
    setCancelGroupOtherText('');
    const n = group.sittings.length;
    scheduleCancel(
      async () => {
        let failed = 0;
        for (const sitting of group.sittings) {
          const r = await apiCancelSitting(sitting.plan_id, sitting.sitting_id, { reason });
          if (!r?.success) failed += 1;
        }
        if (failed > 0) {
          setCancelError({
            sittingId: `group-${group.visit_id}`,
            procName: `${failed} of ${n} for ${group.patient_name}`,
            message: 'Some cancels failed. Refresh and retry.',
          });
        } else {
          // eslint-disable-next-line no-console
          console.info('metric=receptionist_cancel_group value=ok');
        }
        reload();
      },
      {
        delayMs: 5000,
        label: `Cancelling ${n} procedure${n === 1 ? '' : 's'} for ${group.patient_name}`,
        key: `group-${group.visit_id}`,
      },
    );
  };

  // Inline payment panel lives inside the selected group card now —
  // see the visit-total slot below. No global footer.

  return (
    <>
      <RoleTaskBadge
        count={totalCount}
        countLabel={totalCount === 1 ? 'TO DO' : 'TO DO'}
        title="Action pending"
        subtitle={
          (sittingsCount > 0 || actionVisitsCount > 0)
            ? `💰 ${sittingsCount} payments  ·  🟠 ${actionVisitsCount} referrals`
            : null
        }
        tone="amber"
        verticalAnchorPx={verticalAnchorPx}
        ariaLabel={`Action pending across ${totalCount} visit${totalCount === 1 ? '' : 's'}`}
        metricKey="action_pending"
      >
        {({ palette }) => (
          <>
            {/* Undo toasts stack at the top — newest last */}
            {deferredCancels.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {deferredCancels.map((p) => (
                  <UndoToast
                    key={p.id}
                    label={p.label}
                    expiresAt={p.expiresAt}
                    onUndo={() => {
                      // eslint-disable-next-line no-console
                      console.info('metric=receptionist_cancel_undo_used value=undone');
                      undoCancel(p.id);
                    }}
                  />
                ))}
              </div>
            )}

            {cancelError && (
              <div
                role="alert"
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fda4af',
                  borderRadius: 8,
                  padding: '0.5rem 0.625rem',
                  fontSize: '0.75rem',
                  color: '#9f1239',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
                className="dark:!bg-rose-900/30 dark:!border-rose-700 dark:!text-rose-200"
              >
                <span aria-hidden="true">⚠</span>
                <span style={{ flex: 1 }}>
                  Could not cancel <strong>{cancelError.procName}</strong>: {cancelError.message}
                </span>
                <button
                  type="button"
                  onClick={() => setCancelError(null)}
                  style={{
                    border: 'none', background: 'transparent',
                    color: 'inherit', cursor: 'pointer', fontSize: '0.875rem',
                    padding: '0 4px',
                  }}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Filter bar: smart input + sticky Past chip. Sibling of the
                body — its re-renders never collapse the open panel. */}
            <div style={{ position: 'relative', marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <label
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0.35rem 0.5rem',
                    borderRadius: 8,
                    background: palette.bg,
                    border: `1px solid ${palette.border}`,
                    minWidth: 0,
                  }}
                  className="focus-within:!border-amber-500"
                >
                  <span aria-hidden="true" style={{ color: palette.muted, fontSize: '0.8125rem' }}>🔍</span>
                  {/* In-input chip — surfaces the parsed Q-token so the
                      receptionist sees the canonical form even if they
                      typed `q1`. */}
                  {parsedQuery.intent === 'queue' && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: '#fef3c7',
                        color: '#92400e',
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                      }}
                      className="dark:!bg-amber-900/40 dark:!text-amber-200"
                    >
                      Q-{parsedQuery.value}
                    </span>
                  )}
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => {
                      // small delay so a mousedown commit on a
                      // suggestion row can run before blur tears the
                      // dropdown down.
                      setTimeout(() => setInputFocused(false), 150);
                    }}
                    placeholder="Search name, mobile, or Q-1 / Q-4…"
                    aria-label="Search by patient name, mobile number, or queue number"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      fontSize: '0.8125rem',
                      color: palette.text,
                    }}
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label="Clear search"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: palette.muted,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        padding: '0 2px',
                      }}
                    >
                      ✕
                    </button>
                  )}
                </label>
                {(hasPastPending || hasFuturePending) && (
                  <button
                    type="button"
                    onClick={() => setTodayOnly((t) => !t)}
                    aria-pressed={todayOnly}
                    title="Show only today's procedures (hide past + scheduled)"
                    className={todayOnly
                      ? 'rxw-anim bg-amber-600 text-white border border-amber-700 hover:bg-amber-700'
                      : 'rxw-anim bg-white text-slate-700 border border-slate-300 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700'}
                    style={{
                      flexShrink: 0,
                      padding: '6px 12px',
                      borderRadius: 8,
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
                    }}
                  >
                    🕒 Today
                  </button>
                )}
              </div>

              {inputFocused && suggestions.length > 0 && (
                <div
                  role="listbox"
                  aria-label="Search suggestions"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    background: palette.bg,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 8,
                    boxShadow: isDark
                      ? '0 8px 24px rgba(0,0,0,0.45)'
                      : '0 8px 24px rgba(15,23,42,0.12)',
                    overflow: 'hidden',
                  }}
                >
                  {suggestions.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setQuery(s.canonical);
                        setInputFocused(false);
                      }}
                      className="hover:bg-amber-50 dark:hover:bg-amber-900/30"
                      style={{
                        display: 'flex',
                        width: '100%',
                        gap: 8,
                        alignItems: 'center',
                        padding: '0.4rem 0.6rem',
                        border: 'none',
                        borderBottom: `1px solid ${palette.border}`,
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span aria-hidden="true" style={{ fontSize: '0.9375rem' }}>{s.icon}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: palette.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.label}
                        </span>
                        <span style={{ display: 'block', fontSize: '0.6875rem', color: palette.muted }}>
                          {s.sub}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {filterActive && (
              <p style={{ margin: 0, fontSize: '0.6875rem', color: palette.muted }}>
                {visibleSittingsCount + visibleActionVisitsCount} of {sittingsCount + actionVisitsCount} matches
              </p>
            )}

            {visibleSittingsCount === 0 && visibleActionVisitsCount === 0 && deferredCancels.length === 0 && (
              <p style={{ margin: 0, fontSize: '0.875rem', color: palette.muted, textAlign: 'center', padding: '1rem 0' }}>
                {filterActive
                  ? `No matches${query.trim() ? ` for "${query.trim()}"` : ''}${todayOnly ? ' today' : ''}. Clear to see all.`
                  : '✅ All clear! No pending tasks.'}
              </p>
            )}

            {visibleSittingsCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: palette.text }}>
                  💰 Procedure payments
                </span>
                <span style={{ fontSize: '0.6875rem', color: palette.muted }}>
                  {visibleSittingsCount} patient{visibleSittingsCount === 1 ? '' : 's'}
                </span>
              </div>
            )}

            {visibleGroups.map((group) => {
              const groupSittingIds = group.sittings.map((s) => s.sitting_id);
              const groupHasSelection = selection.visitId === group.visit_id && selection.sittingIds.size > 0;
              const allSelectedInGroup = groupHasSelection
                && groupSittingIds.every((id) => selection.sittingIds.has(id));
              const someSelectedInGroup = groupHasSelection && !allSelectedInGroup;
              return (
              <div
                key={group.visit_id || group.sittings[0]?.sitting_id}
                ref={(el) => {
                  if (el && group.visit_id) cardRefs.current[group.visit_id] = el;
                }}
                className="rxw-anim"
                style={{
                  border: `1px solid ${palette.border}`,
                  // Single selection signal: a 4px amber left bar +
                  // a soft warm shadow. The compound border/wash/tint
                  // approach from before read as busy.
                  borderLeft: groupHasSelection
                    ? `4px solid ${palette.accent}`
                    : `1px solid ${palette.border}`,
                  borderRadius: 12,
                  background: palette.bgAlt,
                  boxShadow: groupHasSelection
                    ? (isDark ? '0 4px 12px rgba(0,0,0,0.35)' : '0 4px 12px rgba(217,119,6,0.12)')
                    : 'none',
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  // Phase 4.0d fix: keep visible overflow so the action
                  // row inside the first procedure card isn't clipped
                  // when subsequent visit-group cards lay out below it.
                }}
              >
                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={allSelectedInGroup}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelectedInGroup;
                      }}
                      onChange={() => (allSelectedInGroup ? clearSelection() : selectAllInGroup(group))}
                      aria-label={`Select every procedure for ${group.patient_name}`}
                      style={{ cursor: 'pointer', width: 14, height: 14, flexShrink: 0 }}
                    />
                    {group.queue_number != null && (
                      <span style={{ fontSize: '11px', fontWeight: 600, color: palette.muted }}>
                        Q-{group.queue_number}
                      </span>
                    )}
                    {(() => {
                      const tl = groupTemporalLabel(group);
                      if (!tl) return null;
                      const tone = TEMPORAL_TONE[tl.tone] || TEMPORAL_TONE.today;
                      return (
                        <span
                          title={tl.text === 'Today' ? "Today's procedure" : `Dated ${tl.text}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                            padding: '2px 8px',
                            borderRadius: 9999,
                            fontSize: '10px',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            background: isDark ? tone.darkBg : tone.bg,
                            color: isDark ? tone.darkFg : tone.fg,
                          }}
                        >
                          <span aria-hidden="true">{tl.icon}</span>
                          {tl.text}
                        </span>
                      );
                    })()}
                    <strong style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: palette.text,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {group.patient_name}
                    </strong>
                    {group.patient_mobile && (
                      <PhoneButton
                        phoneNumber={group.patient_mobile}
                        isDark={isDark}
                        size="sm"
                      />
                    )}
                    <span style={{ fontSize: '11px', color: palette.muted, fontWeight: 500 }}>
                      {visitStatusLabel(group.visit_status)}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setCancelGroupTarget(group.visit_id); setCancelGroupOtherText(''); }}
                      aria-label={`Cancel all procedures for ${group.patient_name}`}
                      title="Cancel all procedures for this visit"
                      className="rxw-anim text-slate-400 hover:text-rose-600 hover:bg-rose-50
                                 dark:text-slate-500 dark:hover:text-rose-300 dark:hover:bg-rose-900/30"
                      style={{
                        flexShrink: 0,
                        marginLeft: 4,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        width: 24,
                        height: 24,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 9999,
                        fontSize: '12.5px',
                        lineHeight: 1,
                        transition: 'background-color 150ms ease, color 150ms ease',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  {group.visit_doctor_name && (
                    <div style={{ fontSize: '11px', color: palette.muted, paddingLeft: 22 }}>
                      With Dr. {group.visit_doctor_name}
                    </div>
                  )}
                </div>

                <div style={{
                  borderTop: `1px solid ${palette.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  // Zero padding + zero gap: each procedure card now owns
                  // its own padding and uses borderTop to separate. This
                  // eliminates the nested padding that was eating into
                  // the action-row's vertical space and producing the
                  // "buttons clipped behind next patient" look.
                  padding: 0,
                  gap: 0,
                }}>
                  {group.sittings.map((s) => {
                    const cost = chipCost(s);
                    const isSelected = selection.sittingIds.has(s.sitting_id);
                    const procName = s.procedure_name || s.procedure_snapshot?.name || '(procedure)';
                    const isCancelTarget = cancelTarget === s.sitting_id;
                    if (isCancelTarget) {
                      const otherValid = cancelOtherText.trim().length >= 3;
                      return (
                        <div
                          key={s.sitting_id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            padding: '0.5rem 0.625rem',
                            borderRadius: 8,
                            background: 'rgba(225,29,72,0.08)',
                            border: '1px solid rgba(225,29,72,0.25)',
                          }}
                        >
                          <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#9f1239' }}>
                            Why are you cancelling {procName}?
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            <button
                              type="button"
                              onClick={() => fireCancel(s, 'Patient opted out')}
                              className="px-2 py-1 rounded-md border bg-white text-slate-800 hover:bg-slate-100
                                         dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                              style={{ fontSize: '0.6875rem' }}
                            >
                              Patient opted out
                            </button>
                            <button
                              type="button"
                              onClick={() => fireCancel(s, 'Patient doing this externally')}
                              className="px-2 py-1 rounded-md border bg-white text-slate-800 hover:bg-slate-100
                                         dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                              style={{ fontSize: '0.6875rem' }}
                            >
                              Doing externally
                            </button>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input
                              type="text"
                              value={cancelOtherText}
                              onChange={(e) => setCancelOtherText(e.target.value)}
                              placeholder="Other reason (3+ chars)…"
                              className="bg-white text-slate-800 border-slate-300
                                         dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600"
                              style={{
                                flex: 1,
                                padding: '0.3rem 0.5rem',
                                borderRadius: 6,
                                borderWidth: 1,
                                borderStyle: 'solid',
                                fontSize: '0.6875rem',
                                outline: 'none',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => fireCancel(s, cancelOtherText.trim())}
                              disabled={!otherValid}
                              className="px-2.5 py-1 rounded-md bg-rose-600 text-white text-xs font-semibold
                                         hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => { setCancelTarget(null); setCancelOtherText(''); }}
                              className="px-2 py-1 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                                         dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                              style={{ fontSize: '0.6875rem' }}
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={s.sitting_id}
                        className="rxw-anim"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          fontSize: '11px',
                          padding: '8px 12px',
                          borderRadius: 0,
                          // Single-signal rule: don't tint per-sitting
                          // rows when selected. The card's amber left
                          // bar conveys "this group is selected"; the
                          // checkbox conveys "this row is checked."
                          background: palette.bg,
                          borderTop: `1px solid ${palette.border}`,
                          borderBottom: 'none',
                          transition: 'background-color 150ms ease',
                        }}
                      >
                        {/* Row 1: checkbox + procedure name + cost */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            minWidth: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(s)}
                            aria-label={`Select ${procName} for collection`}
                          />
                          <span
                            title={procName}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 500,
                              fontSize: '12.5px',
                              color: palette.text,
                            }}
                          >
                            {procName}
                          </span>
                          <span style={{
                            fontWeight: 600,
                            flexShrink: 0,
                            fontSize: '12.5px',
                            color: isDark ? '#fcd34d' : '#92400e',
                          }}>
                            {formatRupees(cost)}
                          </span>
                        </label>

                        {/* Row 2: action buttons — flex-wrap so they
                            never clip on narrow viewports. Hidden when
                            the receptionist has ticked any checkbox on
                            this visit; the inline payment panel below
                            takes over from here. */}
                        {!groupHasSelection && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 4,
                            alignItems: 'center',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              handleAction({ action: 'collect_payment', sitting: s })
                            }
                            title={`Take ₹${Math.round(cost)} now`}
                            className="rxw-anim rounded-lg bg-amber-600 text-white font-semibold
                                       hover:bg-amber-700
                                       dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-slate-900"
                            style={{
                              padding: '6px 12px',
                              fontSize: '12.5px',
                              transition: 'background-color 150ms ease',
                            }}
                          >
                            💳 Take ₹{Math.round(cost)}
                          </button>
                          {SHOW_PER_SITTING_BOOKING_ACTIONS && (
                          <button
                            type="button"
                            onClick={() =>
                              setScheduleTarget({ sitting: s, variant: 'schedule' })
                            }
                            aria-label={`Book slot for ${procName} (no payment)`}
                            title="Book a slot now, take payment later"
                            className="rxw-anim rounded-lg border bg-white text-slate-700 hover:bg-slate-100
                                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                            style={{
                              padding: '6px 10px',
                              fontSize: '11px',
                              fontWeight: 500,
                              borderColor: '#cbd5e1',
                              transition: 'background-color 150ms ease',
                            }}
                          >
                            📅 Book slot
                          </button>
                          )}
                          {SHOW_PER_SITTING_BOOKING_ACTIONS && (
                          <button
                            type="button"
                            onClick={() =>
                              setScheduleTarget({ sitting: s, variant: 'collectSchedule' })
                            }
                            aria-label={`Take ₹${Math.round(cost)} and book slot for ${procName}`}
                            title={`Take ₹${Math.round(cost)} and book a slot in one step`}
                            className="rxw-anim rounded-lg border bg-amber-50 text-amber-900 hover:bg-amber-100
                                       dark:bg-amber-900/30 dark:text-amber-100 dark:hover:bg-amber-900/50 dark:border-amber-700"
                            style={{
                              padding: '6px 10px',
                              fontSize: '11px',
                              fontWeight: 600,
                              borderColor: '#fcd34d',
                              transition: 'background-color 150ms ease',
                            }}
                          >
                            💳 Take + 📅 Book
                          </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setCancelTarget(s.sitting_id);
                              setCancelOtherText('');
                            }}
                            aria-label={`Cancel ${procName}`}
                            title="Cancel this procedure"
                            className="rxw-anim rounded-lg border bg-white text-rose-700 hover:bg-rose-50
                                       dark:bg-slate-800 dark:text-rose-200 dark:hover:bg-rose-900/30 dark:border-rose-700"
                            style={{
                              padding: '6px 10px',
                              fontSize: '11px',
                              fontWeight: 500,
                              borderColor: '#fda4af',
                              transition: 'background-color 150ms ease, color 150ms ease',
                            }}
                          >
                            ✕ Cancel
                          </button>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {cancelGroupTarget === group.visit_id ? (
                  <div
                    style={{
                      borderTop: `1px solid ${palette.border}`,
                      padding: '0.5rem 0.625rem',
                      background: 'rgba(225,29,72,0.08)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#9f1239' }}>
                      Cancel all {group.sittings.length} procedure{group.sittings.length === 1 ? '' : 's'} for {group.patient_name}?
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <button
                        type="button"
                        onClick={() => fireCancelGroup(group, 'Patient opted out')}
                        className="px-2 py-1 rounded-md border bg-white text-slate-800 hover:bg-slate-100
                                   dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                        style={{ fontSize: '0.6875rem' }}
                      >
                        Patient opted out
                      </button>
                      <button
                        type="button"
                        onClick={() => fireCancelGroup(group, 'Patient doing this externally')}
                        className="px-2 py-1 rounded-md border bg-white text-slate-800 hover:bg-slate-100
                                   dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                        style={{ fontSize: '0.6875rem' }}
                      >
                        Doing externally
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input
                        type="text"
                        value={cancelGroupOtherText}
                        onChange={(e) => setCancelGroupOtherText(e.target.value)}
                        placeholder="Other reason (3+ chars)…"
                        className="bg-white text-slate-800 border-slate-300
                                   dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600"
                        style={{
                          flex: 1,
                          padding: '0.3rem 0.5rem',
                          borderRadius: 6,
                          borderWidth: 1,
                          borderStyle: 'solid',
                          fontSize: '0.6875rem',
                          outline: 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => fireCancelGroup(group, cancelGroupOtherText.trim())}
                        disabled={cancelGroupOtherText.trim().length < 3}
                        className="px-2.5 py-1 rounded-md bg-rose-600 text-white text-xs font-semibold
                                   hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel all
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCancelGroupTarget(null); setCancelGroupOtherText(''); }}
                        className="px-2 py-1 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                                   dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                        style={{ fontSize: '0.6875rem' }}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                ) : groupHasSelection ? (
                  <div
                    className="rxw-fade-in"
                    style={{
                      borderTop: `2px solid ${palette.accent}`,
                      padding: '12px',
                      background: isDark ? 'rgba(217,119,6,0.10)' : '#fffbeb',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      animation: 'rxw-fade-in 180ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}>
                      <span style={{ fontSize: '11px', color: palette.muted, fontWeight: 500 }}>
                        {selectedSittings.length} of {group.sittings.length} selected
                      </span>
                      <strong style={{
                        fontSize: '15px',
                        fontWeight: 700,
                        color: isDark ? '#fcd34d' : '#92400e',
                      }}>
                        Total {formatRupees(selectedTotal)}
                      </strong>
                    </div>
                    <div>
                      <div style={{
                        fontSize: '11px',
                        color: palette.muted,
                        marginBottom: 6,
                        fontWeight: 500,
                      }}>
                        Payment mode
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {PAYMENT_MODES.map((pm) => {
                          const sel = paymentMode === pm.id;
                          return (
                            <button
                              key={pm.id}
                              type="button"
                              onClick={() => setPaymentMode(pm.id)}
                              aria-pressed={sel}
                              className="rxw-anim"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '6px 12px',
                                borderRadius: 9999,
                                fontSize: '12.5px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
                                // Amber-aligned active state — the payment
                                // workflow on this surface is amber, not
                                // the teal used in the register form.
                                border: sel
                                  ? '2px solid #d97706'
                                  : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'}`,
                                background: sel
                                  ? (isDark ? 'rgba(217,119,6,0.18)' : '#fef3c7')
                                  : (isDark ? 'rgba(255,255,255,0.04)' : '#ffffff'),
                                color: sel
                                  ? (isDark ? '#fcd34d' : '#92400e')
                                  : (isDark ? '#cbd5e1' : '#475569'),
                              }}
                            >
                              <span aria-hidden="true">{pm.icon}</span>
                              {pm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        onClick={collectSelected}
                        disabled={batchBusy || !paymentMode}
                        title={paymentMode
                          ? `Take ${formatRupees(selectedTotal)} from ${selectedPatientName || group.patient_name}`
                          : 'Pick a payment mode first'}
                        className="rxw-anim flex-1 bg-amber-600 text-white font-semibold
                                   hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed
                                   dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-slate-900"
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          fontSize: '12.5px',
                          transition: 'background-color 150ms ease',
                        }}
                      >
                        {batchBusy
                          ? 'Taking…'
                          : `💳 Take ${formatRupees(selectedTotal)} from ${selectedPatientName || group.patient_name}`}
                      </button>
                      <button
                        type="button"
                        onClick={clearSelection}
                        disabled={batchBusy}
                        className="rxw-anim border bg-white text-slate-700 hover:bg-slate-100
                                   dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          fontSize: '11px',
                          fontWeight: 500,
                          transition: 'background-color 150ms ease',
                        }}
                      >
                        Clear
                      </button>
                    </div>
                    {batchError && (
                      <p style={{ margin: 0, fontSize: '11px', color: '#b91c1c' }}>
                        {batchError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div style={{
                    padding: '8px 12px',
                    borderTop: `1px solid ${palette.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '11px',
                    color: palette.muted,
                  }}>
                    <span>Visit total</span>
                    <strong style={{ color: palette.text, fontWeight: 600 }}>
                      {formatRupees(group.total)}
                    </strong>
                  </div>
                )}
              </div>
              );
            })}

            {visibleActionVisitsCount > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: visibleSittingsCount > 0 ? 8 : 0 }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: palette.text }}>
                    🟠 Doctor referrals
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: palette.muted }}>
                    {visibleActionVisitsCount} patient{visibleActionVisitsCount === 1 ? '' : 's'}
                  </span>
                </div>
                {visibleActionVisits.map((v) => {
                  const isReferral = v.action_pending_intent === INTENTS.REFERRAL_TO_DOCTOR;
                  // Phase 3.5 — `payment_due` is rupees on the visit row;
                  // collected is still paise. Compare both in paise.
                  const dueRupees = visitPaymentDueRupees(v);
                  const due = Math.round(dueRupees * 100);
                  const collected = Number(v.action_pending_payment_collected_paise || 0);
                  const fullyCollected = due > 0 && collected >= due;
                  const noPaymentNeeded = due === 0;
                  const refPalette = INTENT_CATEGORY_PALETTE.REFERRAL;
                  return (
                    <div
                      key={v.visit_id}
                      style={{
                        border: `1px solid ${palette.border}`,
                        borderRadius: 10,
                        background: palette.bgAlt,
                        padding: '0.5rem 0.625rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        {v.queue_number != null && (
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: palette.muted }}>
                            Q-{v.queue_number}
                          </span>
                        )}
                        <strong style={{ fontSize: '0.9375rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {v.patient_name || v.visit_id}
                        </strong>
                        {isReferral && refPalette && (
                          <span
                            style={{
                              fontSize: '0.625rem',
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: '#ffedd5',
                              color: '#9a3412',
                            }}
                          >
                            {refPalette.icon} Referral
                          </span>
                        )}
                      </div>
                      {isReferral && (
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#9a3412', marginTop: 2 }}>
                          → Send to {formatDoctorLabel(v.action_pending_target_doctor_id)}
                          {due > 0 && ` · ₹${(due / 100).toFixed(0)} to take`}
                          {collected > 0 && ` · ✓ ₹${(collected / 100).toFixed(0)} taken`}
                        </div>
                      )}
                      {v.doctor_name && (
                        <div style={{ fontSize: '0.6875rem', color: palette.muted }}>
                          From Dr. {v.doctor_name}
                        </div>
                      )}
                      {isReferral && v.action_pending_referral_reason && (
                        <div
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: palette.text,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          Reason: {v.action_pending_referral_reason}
                        </div>
                      )}
                      {!isReferral && v.action_pending_intent && (
                        <div style={{ fontSize: '0.75rem', color: palette.muted }}>
                          {v.action_pending_intent}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                        {isReferral && !fullyCollected && !noPaymentNeeded && (
                          <button
                            type="button"
                            onClick={() => setReferralCollect(v)}
                            title={`Take ₹${(due / 100).toFixed(0)} now, send later`}
                            className="px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                            style={{ fontSize: '0.8125rem', fontWeight: 700 }}
                          >
                            Take ₹{(due / 100).toFixed(0)}
                          </button>
                        )}
                        {isReferral && (
                          <button
                            type="button"
                            onClick={() => setReferralResolve(v)}
                            title={
                              fullyCollected || noPaymentNeeded
                                ? `Send patient to ${formatDoctorLabel(v.action_pending_target_doctor_id)}`
                                : `Take ₹${(due / 100).toFixed(0)} and send to ${formatDoctorLabel(v.action_pending_target_doctor_id)}`
                            }
                            className="px-3 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700"
                            style={{ fontSize: '0.8125rem', fontWeight: 700 }}
                          >
                            {fullyCollected || noPaymentNeeded
                              ? 'Send →'
                              : `Take ₹${(due / 100).toFixed(0)} + Send →`}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate(`${ROUTES.ORG}#visit=${v.visit_id}`)}
                          title="Open the full visit page"
                          className="px-3 py-1.5 rounded-md border bg-white text-slate-800 hover:bg-slate-100
                                     dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
                          style={{ fontSize: '0.75rem', borderColor: '#cbd5e1' }}
                        >
                          Full details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </RoleTaskBadge>

      <SchedulePickerDialog
        open={!!scheduleTarget}
        variant={scheduleTarget?.variant || 'schedule'}
        procedureName={
          scheduleTarget?.sitting?.procedure_name
          || scheduleTarget?.sitting?.procedure_snapshot?.name
          || 'procedure'
        }
        defaultAmount={chipCost(scheduleTarget?.sitting)}
        busy={scheduleBusy}
        error={scheduleError}
        onConfirm={handleScheduleConfirm}
        onClose={closeScheduleDialog}
      />

      {referralCollect && (
        <CollectReferralPaymentDialog
          visit={referralCollect}
          onSubmitted={() => {
            setReferralCollect(null);
            reloadActionVisits();
          }}
          onCancel={() => setReferralCollect(null)}
        />
      )}

      {referralResolve && (
        <ReferralResolveDialog
          visit={referralResolve}
          onSubmitted={() => {
            setReferralResolve(null);
            reloadActionVisits();
          }}
          onCancel={() => setReferralResolve(null)}
        />
      )}

      {dialogs}
    </>
  );
}
